import {
  IObservableProperty,
  IReadOnlyObservableProperty,
  ObservableProperty,
} from "../../observable/observableProperty";
import { IPuzzleV1 } from "./interfaces/classes/IPuzzleV1";
import { SimpleDate, areSimpleDatesEqual } from "../SimpleDate";
import { getSimpleDateInSeattle } from "../getSimpleDateInSeattle";
import { IWordInPuzzleDataV1 } from "./interfaces/data/IWordInPuzzleDataV1";
import { IScoringRubricV1 } from "./interfaces/data/IScoringRubricV1";
import { IWordInPuzzleV1, RarityStatus } from "./interfaces/classes/IWordInPuzzleV1";
import { WordInPuzzleV1 } from "./WordInPuzzleV1";
import { IPuzzleRemoteDataV1 } from "./interfaces/data/IPuzzleRemoteDataV1";

export class PuzzleV1 implements IPuzzleV1 {
  private _data: IPuzzleRemoteDataV1;
  public readonly _foundWords: IObservableProperty<IWordInPuzzleV1[]>;
  public readonly _allWords: IObservableProperty<IWordInPuzzleV1[]>;
  public readonly _totalScore: IObservableProperty<number>;
  public readonly _regularPoints: IObservableProperty<number | undefined>;
  public readonly _rarityPoints: IObservableProperty<number | undefined>;
  /** Path taken by the player for each found word, keyed by uppercase word. */
  private readonly _wordPaths: Map<string, number[][]> = new Map();

  public get wordPaths(): ReadonlyMap<string, number[][]> {
    return this._wordPaths;
  }

  public get timeElapsedInSeconds(): number {
    return this._data.timeElapsedInSeconds || 0;
  }

  public set timeElapsedInSeconds(newValue: number) {
    this._data.timeElapsedInSeconds = Math.min(this._data.timeLimitInSeconds, newValue);
  }

  public get timeLimitInSeconds(): number {
    return this._data.timeLimitInSeconds;
  }

  public get totalPlays(): number | undefined {
    return this._data.solution.totalSubmissions;
  }

  public get scoringRubric(): IScoringRubricV1 {
    return this._data.scoringRubric;
  }

  public get date(): SimpleDate {
    return this._data.date;
  }

  public get board(): string[][] {
    return this._data.board;
  }

  public get played(): boolean {
    return this._data.played;
  }

  public set played(newValue: boolean) {
    this._data.played = newValue;
  }

  public get foundWords(): IReadOnlyObservableProperty<IWordInPuzzleV1[]> {
    return this._foundWords;
  }
  public get allWords(): IReadOnlyObservableProperty<IWordInPuzzleV1[]> {
    return this._allWords;
  }
  public get totalScore(): IReadOnlyObservableProperty<number> {
    return this._totalScore;
  }
  public get regularPoints(): IReadOnlyObservableProperty<number | undefined> {
    return this._regularPoints;
  }
  public get rarityPoints(): IReadOnlyObservableProperty<number | undefined> {
    return this._rarityPoints;
  }

  public get isToday(): boolean {
    return areSimpleDatesEqual(getSimpleDateInSeattle(), this._data.date);
  }

  public get isPlayable(): boolean {
    return !this._data.played && this.isToday;
  }

  public constructor(data: IPuzzleRemoteDataV1) {
    this._data = data;

    // Build the master list of words. Start with the server-known list (from solution.wordsInPuzzle),
    // then add any locally-found words that aren't in it (today's puzzle has no solution yet, so
    // wordsInPuzzle is empty and ALL found words must be added dynamically).
    const allWordsList: IWordInPuzzleV1[] = [];
    const seenWords = new Set<string>();

    this._foundWords = new ObservableProperty<IWordInPuzzleV1[]>([]);

    for (const wordInPuzzleData of data.solution.wordsInPuzzle) {
      const upper = wordInPuzzleData.word.toUpperCase();
      const isFound =
        data.wordsFound.findIndex((candidate: string) => upper === candidate.toUpperCase()) !== -1;

      const wordInPuzzle = new WordInPuzzleV1(
        wordInPuzzleData.word,
        this._getRegularPointsForWord(wordInPuzzleData.word),
        this._getRarityStatusForWord(wordInPuzzleData),
        wordInPuzzleData.timesFound,
        isFound,
      );

      allWordsList.push(wordInPuzzle);
      seenWords.add(upper);
      if (isFound) {
        this._foundWords.value.push(wordInPuzzle);
      }
    }

    // For words found locally that aren't in solution.wordsInPuzzle yet (today's puzzle case),
    // create entries on the fly so the player's progress is preserved across reloads.
    for (const localWord of data.wordsFound) {
      const upper = localWord.toUpperCase();
      if (seenWords.has(upper)) continue;

      const wordInPuzzle = new WordInPuzzleV1(
        localWord,
        this._getRegularPointsForWord(localWord),
        undefined,
        1,
        true,
      );
      allWordsList.push(wordInPuzzle);
      this._foundWords.value.push(wordInPuzzle);
      seenWords.add(upper);
    }

    this._allWords = new ObservableProperty<IWordInPuzzleV1[]>(allWordsList);
    this._totalScore = new ObservableProperty<number>(data.totalPoints || 0);
    this._regularPoints = new ObservableProperty<number | undefined>(data.regularPoints);
    this._rarityPoints = new ObservableProperty<number | undefined>(data.rarityPoints);
    this.calculateRegularPoints();
  }

  public addFoundWord(word: string, path: number[][]): number {
    const upper = word.toUpperCase();
    this._wordPaths.set(upper, path);

    let wordInPuzzle = this._allWords.value.find(
      (candidate: IWordInPuzzleV1) => candidate.word.toUpperCase() === upper,
    );

    // Today's puzzle has no pre-populated word list. Create the entry on the fly
    // using the puzzle's scoring rubric. Rarity is unknown until next-day rollover.
    if (!wordInPuzzle) {
      wordInPuzzle = new WordInPuzzleV1(
        word,
        this._getRegularPointsForWord(word),
        undefined,
        1,
        false,
      );
      const newAllWords = this._allWords.value.slice();
      newAllWords.push(wordInPuzzle);
      this._allWords.value = newAllWords;
    }

    if (wordInPuzzle.isFound) {
      console.warn("Tried to mark a word found that was already found!");
      return 0;
    }

    // Update the word now that it was found
    wordInPuzzle.isFound = true;
    wordInPuzzle.timesFound = (wordInPuzzle.timesFound || 0) + 1;

    // Add the word to the foundWords list, triggering a change notification
    const newFoundWordList = this._foundWords.value.slice();
    newFoundWordList.push(wordInPuzzle);
    this._foundWords.value = newFoundWordList;

    // Since we updated the word in the allWords list, trigger a change notification for it too
    this._allWords.value = this._allWords.value.slice();

    this.calculateRegularPoints();
    return wordInPuzzle.regularPoints;
  }

  public serialize(): IPuzzleRemoteDataV1 {
    return {
      date: this._data.date,
      board: this._data.board,
      played: this.played,
      wordsFound: this.foundWords.value.map((wordInPuzzle: IWordInPuzzleV1) =>
        wordInPuzzle.serialize(),
      ),
      scoringRubric: this._data.scoringRubric,
      solution: this._data.solution,
      timeLimitInSeconds: this._data.timeLimitInSeconds,
      timeElapsedInSeconds: this._data.timeElapsedInSeconds,
      // Read from the live observables so locally-computed scores survive a save+reload cycle.
      totalPoints: this._totalScore.value,
      regularPoints: this._regularPoints.value ?? 0,
      rarityPoints: this._rarityPoints.value,
    };
  }

  private calculateRegularPoints(): void {
    let regularPoints = 0;
    if (this.foundWords.value) {
      this.foundWords.value.forEach((foundWord: IWordInPuzzleV1) => {
        regularPoints += foundWord.regularPoints;
      });
    }
    this._regularPoints.value = regularPoints;
    // Keep totalScore in sync so the home hero, day cards, and resume tile show
    // the live score for an in-progress puzzle, not just after server submit.
    this._totalScore.value = regularPoints + (this._rarityPoints.value ?? 0);
  }

  private _getRegularPointsForWord(word: string): number {
    // The rubric array is indexed by (word length - 1): index 2 = points for a
    // 3-letter word; indices 0-1 are 0 for invalid lengths. This must match the
    // canonical convention used by the puzzle generator (wirder-bigbang) and the
    // backend scorer, which are authoritative for stored scores, stats, and
    // rankings. Words longer than the array score the top (last) tier.
    const points = this._data.scoringRubric.wordLengthPoints;
    if (points.length === 0 || word.length < 1) {
      return 0;
    }
    const index = Math.min(word.length - 1, points.length - 1);
    return points[index];
  }

  // TODO: start storing rarity status in the solution list server side then just read it on the client

  private _getRarityStatusForWord(wordInPuzzleData: IWordInPuzzleDataV1): RarityStatus | undefined {
    // If totalSubmissions are undefined (today's puzzle), or 0, RarityStatus is undefined
    if (!this._data.solution.totalSubmissions) {
      return undefined;
    }

    // If it hasn't been found at all, this will be undefined
    if (wordInPuzzleData.timesFound === undefined) {
      return undefined;
    }

    const percentFound = (wordInPuzzleData.timesFound * 100) / this._data.solution.totalSubmissions;

    if (
      wordInPuzzleData.timesFound === 1 ||
      percentFound <= this._data.scoringRubric.goldCriteria.maxPercent
    ) {
      return {
        rarityLevel: "Gold",
        bonus: this._data.scoringRubric.goldCriteria.bonus,
      };
    }
    if (
      wordInPuzzleData.timesFound === 2 ||
      percentFound <= this._data.scoringRubric.silverCriteria.maxPercent
    ) {
      return {
        rarityLevel: "Silver",
        bonus: this._data.scoringRubric.silverCriteria.bonus,
      };
    }
    if (
      wordInPuzzleData.timesFound === 3 ||
      percentFound <= this._data.scoringRubric.bronzeCriteria.maxPercent
    ) {
      return {
        rarityLevel: "Bronze",
        bonus: this._data.scoringRubric.bronzeCriteria.bonus,
      };
    }

    return undefined;
  }
}
